import { randomUUID } from 'client/utils';
import { createContext, useMemo, useState } from 'react';
import calApi from '../api/calApi';
import {
    Booking,
    CalBookingProps,
    CalEventData,
    EventServerModel,
} from '../types';
import { resolveCalProviderLanguage } from '../utils';

// State interface
export interface RuntimeCalBookingState {
    isLoading: boolean;
    confirmButtonDisabled: boolean;
    bookerKey: string | null;
    eventSlug: string | null;
    rescheduleUid: string | null;
    cancelBookingId: string | null;
    confirmationData: Booking | null;
    calEventData: CalEventData | null;
    teamId: number | null;
    userName: string | null;
    language: string;
    apiUrl: string;
}

export interface RuntimeCalBookingActions {
    setIsLoading: (isLoading: boolean) => void;
    setConfirmButtonDisabled: (confirmButtonDisabled: boolean) => void;
    resetBookerState: () => void;
    setRescheduleUid: (rescheduleUid: string | null) => void;
    setCancelBookingId: (cancelBookingId: string | null) => void;
    setConfirmationData: (confirmationData: Booking | null) => void;
    clearConfirmationData: () => void;
    onBookingSuccess: (booking: Booking) => void;
    setCalEventData: (calEventData: CalEventData) => void;
    setEventSlug: (eventSlug: string | null) => void;
    setTeamId: (teamId: number | null) => void;
    setUserName: (userName: string | null) => void;
}

// Initial state
export const initialState: RuntimeCalBookingState = {
    isLoading: true,
    confirmButtonDisabled: false,
    bookerKey: randomUUID(),
    eventSlug: null,
    rescheduleUid: null,
    cancelBookingId: null,
    confirmationData: null,
    calEventData: null,
    teamId: null,
    userName: null,
    language: 'en',
    apiUrl: calApi.apiUrl,
};

export const useRuntimeCalBookingState = (props: CalBookingProps) => {
    const [state, setState] = useState<RuntimeCalBookingState>(() => {
        calApi.setCalDomain(props.calApiDomain);
        calApi.setCalClientId(props.clientId);

        const eventSlug =
            props.events && props.events.length === 1
                ? // Backward compatibility with single event booking
                  props.events?.[0]?.slug || props.eventSlug || null
                : null;

        return {
            ...initialState,
            language: resolveCalProviderLanguage(),
            apiUrl: calApi.apiUrl,
            teamId: props.teamId,
            userName: props.userName,
            eventSlug,
        };
    });

    const actions = useMemo(
        () =>
            ({
                setIsLoading: (isLoading: boolean) =>
                    setState((prev) => ({ ...prev, isLoading })),

                setConfirmButtonDisabled: (confirmButtonDisabled: boolean) =>
                    setState((prev) => ({ ...prev, confirmButtonDisabled })),

                resetBookerState: () =>
                    setState((prev) => ({ ...prev, bookerKey: randomUUID() })),

                setRescheduleUid: (rescheduleUid: string | null) =>
                    setState((prev) => ({ ...prev, rescheduleUid })),

                setCancelBookingId: (cancelBookingId: string | null) =>
                    setState((prev) => ({ ...prev, cancelBookingId })),

                setEventSlug: (eventSlug: string | null) =>
                    setState((prev) => ({ ...prev, eventSlug })),

                setConfirmationData: (confirmationData: Booking | null) =>
                    setState((prev) => ({ ...prev, confirmationData })),
                clearConfirmationData: () =>
                    setState((prev) => ({
                        ...prev,
                        confirmationData: null,
                        bookerKey: randomUUID(),
                    })),
                onBookingSuccess: (booking: Booking) =>
                    setState((prev) => ({
                        ...prev,
                        rescheduleUid: null,
                        confirmationData: booking,
                        bookerKey: randomUUID(),
                    })),
                setEvent: (event: EventServerModel) =>
                    setState((prev) => ({ ...prev, event })),

                setCalEventData: (calEventData: CalEventData) =>
                    setState((prev) => ({ ...prev, calEventData })),
                setTeamId: (teamId: number | null) =>
                    setState((prev) => ({ ...prev, teamId })),
                setUserName: (userName: string | null) =>
                    setState((prev) => ({ ...prev, userName })),
            } as RuntimeCalBookingActions),
        []
    );

    return [state, actions] as const;
};

export const BookerStateContext = createContext<
    [RuntimeCalBookingState, RuntimeCalBookingActions]
>([
    initialState,
    {
        setIsLoading: () => {},
        setConfirmButtonDisabled: () => {},
        resetBookerState: () => {},
        setRescheduleUid: () => {},
        setCancelBookingId: () => {},
        setEventSlug: () => {},
        setConfirmationData: () => {},
        clearConfirmationData: () => {},
        onBookingSuccess: () => {},
        setCalEventData: () => {},
        setTeamId: () => {},
        setUserName: () => {},
    } satisfies RuntimeCalBookingActions,
]);
